import React, { useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RiPlaneLine, RiArrowUpLine, RiArrowDownSLine, RiLoader4Line, RiCheckboxCircleLine, RiAddLine } from '@remixicon/react';
import { toast } from 'sonner';
import { cn } from '../../../../components/ui/utils';
import AircraftCard from './AircraftCard';
import EditPricingDrawer from './EditPricingDrawer';
import AddAircraftManuallyDrawer from './AddAircraftManuallyDrawer';
import { SearchAircraft } from '../../../search';
import { useAppStore } from '@store/appStore';
import Modal from '@components/ui/Modal';
import { EmptyState2 } from '@assets/images';
import { usePermissions } from '@/hooks/usePermissions';
import { ROLES, normalizeRole } from '@/lib/permissions';

/**
 * AircraftOptionsSection Component
 * Card-style section displaying available aircraft options with selection functionality
 *
 * @param {Object} props
 * @param {Object} props.selectedAircraft - Currently selected aircraft
 * @param {Function} props.onSelect - Callback when aircraft is selected
 * @param {boolean} props.isComplete - Whether section is complete
 * @param {boolean} props.isExpanded - Whether section is expanded
 * @param {Function} props.onToggle - Callback when section is toggled
 * @param {Function} props.onNext - Callback when Next button is clicked
 * @param {Function} props.onBack - Callback when Back button is clicked
 * @param {boolean} props.isUnlocked - Whether section has been unlocked by clicking Next
 * @param {Array} props.aircraft - Aircraft array (optional, for testing)
 * @param {boolean} props.isLoading - Loading state (optional, for testing)
 */
const AircraftOptionsSection = ({
  formData,
  isComplete = false,
  isExpanded = false,
  onToggle,
  onNext,
  onBack,
  isUnlocked = false,
  onUpdate,
  onBatchUpdate,
  onRemove,
  onSaveAircraftProfile,
  onSaveOperatorNameOverride,
  // Append a freshly-created bid_option to formData.bidOptions (edit /
  // duplicate / trip-group contexts). New options carry no bid_option_id
  // so reconcileBidOptions POSTs them on the next preview-save.
  onAppendBidOption,
  // Source-quote status when in edit mode — used to gate Add Aircraft
  // to pre-send states (DRAFT / PENDING_APPROVAL) so we don't let the
  // broker change the tail lineup after the client has seen it.
  editSourceStatus,
  // Explicit signal from the parent that this GenerateQuote session is
  // editing an existing quote (any of: /quotes/generate?quoteid=…,
  // duplicate flow). Combined with editSourceStatus above.
  isEditing = false,
  isDataLoading,
}) => {
  const aircraftProfiles = formData?.aircraftProfiles || {};
  // Layer the buffered custom_profile from the pricing drawer onto the
  // bidOption's aircraft, so the card / drawer / preview all reflect
  // the broker's edits before persist. `null` in the buffer means
  // revert-to-base — strip any existing custom_profile from the record.
  const applyBufferedProfile = (item) => {
    const aircraftId = item?.aircraft?.aircraft_id;
    if (!aircraftId || !(aircraftId in aircraftProfiles)) return item;
    const buffered = aircraftProfiles[aircraftId];
    return {
      ...item,
      aircraft: {
        ...item.aircraft,
        custom_profile: buffered === null ? null : buffered,
      },
    };
  };
  const [searchParams] = useSearchParams();
  const isQuickQuote = searchParams.has('quickquote');
  const { shortlistAircraft, selectedTripGroup } = useAppStore();

  // "Add Aircraft" render gate. Beyond Quick Quote, also allow adding
  // tails when:
  //  - Editing a DRAFT / PENDING_APPROVAL quote (includes the duplicate
  //    flow, which always arrives as DRAFT). Blocked for SENT / VIEWED /
  //    SIGNED / ACCEPTED / COMPLETED — the client has seen the tail
  //    lineup and changing it mid-negotiation is confusing.
  //  - Trip-group RFQ pricing flow (Shortlist → Generate Quote), which
  //    lands on GenerateQuote with `selectedTripGroup` set but no
  //    ?quoteid= — pre-send by construction.
  const sourceStatusU = (editSourceStatus || 'DRAFT').toUpperCase();
  const isPreSend = ['DRAFT', 'PENDING_APPROVAL'].includes(sourceStatusU);
  const canAddTails =
    isQuickQuote ||
    (isEditing && isPreSend) ||
    (!!selectedTripGroup && !isEditing);
  // Add-Aircraft-Manually is visible to super_admin / admin / broker /
  // junior_broker — every role that can put together a quote. Other
  // CREATE-capable roles (finance etc.) still can't spin up a whole
  // new aircraft record.
  const { role } = usePermissions();
  const canAddAircraftManually = (() => {
    const r = normalizeRole(role);
    return r === ROLES.SUPER_ADMIN
      || r === ROLES.ADMIN
      || r === ROLES.BROKER
      || r === ROLES.JUNIOR_BROKER;
  })();
  const [showSearchAircraft, setShowSearchAircraft] = useState(false);
  const [showAddManually, setShowAddManually] = useState(false);
  const [showEditPricing, setShowEditPricing] = useState(false);
  const [selectedAircraftForEdit, setSelectedAircraftForEdit] = useState(null);

  // Tracks bid_option_ids the broker attempted to progress past while still
  // unpriced. Rendered as a red border on the offending card until pricing
  // is set (`isUnpriced(option)` re-evaluates on every render, so as soon
  // as the drawer save lifts `total_price` above 0, the highlight clears
  // automatically — no manual reset needed).
  const [unpricedFlaggedIds, setUnpricedFlaggedIds] = useState(() => new Set());

  // "Unpriced" = the card is showing a $0.00 total. `total_price` is the
  // field that renders as "Total Cost" on AircraftCard. A freshly added
  // tail starts at "0.00" until the broker opens Edit Pricing and saves;
  // any drawer save writes a non-zero total (taxes / PF always contribute
  // even when operator cost stays 0). Guards against NaN / undefined so
  // legitimately-zero DB rows don't sneak through.
  const isUnpriced = (option) => {
    const raw = parseFloat(option?.total_price);
    return !Number.isFinite(raw) || raw <= 0;
  };

  const handleNext = () => {
    const options = formData?.bidOptions || [];
    const unpriced = options.filter(isUnpriced);
    if (unpriced.length > 0) {
      setUnpricedFlaggedIds(new Set(unpriced.map((o) => o.bid_option_id).filter(Boolean)));
      toast.error(
        unpriced.length === 1
          ? 'Please set pricing on the aircraft before continuing.'
          : `Please set pricing on ${unpriced.length} aircraft before continuing.`,
      );
      return;
    }
    // All priced — clear any lingering flags and advance.
    if (unpricedFlaggedIds.size > 0) setUnpricedFlaggedIds(new Set());
    onNext?.();
  };

  // Drag to scroll refs and state for aircraft slider
  const aircraftSliderRef = useRef(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  const handleMouseDown = (e) => {
    isDragging.current = true;
    startX.current = e.pageX - aircraftSliderRef.current.offsetLeft;
    scrollLeft.current = aircraftSliderRef.current.scrollLeft;
    aircraftSliderRef.current.style.cursor = 'grabbing';
    aircraftSliderRef.current.style.userSelect = 'none';
  };

  const handleMouseLeave = () => {
    isDragging.current = false;
    if (aircraftSliderRef.current) {
      aircraftSliderRef.current.style.cursor = 'grab';
      aircraftSliderRef.current.style.userSelect = '';
    }
  };

  const handleMouseUp = () => {
    isDragging.current = false;
    if (aircraftSliderRef.current) {
      aircraftSliderRef.current.style.cursor = 'grab';
      aircraftSliderRef.current.style.userSelect = '';
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current) return;
    e.preventDefault();
    const x = e.pageX - aircraftSliderRef.current.offsetLeft;
    const walk = (x - startX.current) * 1.5;
    aircraftSliderRef.current.scrollLeft = scrollLeft.current - walk;
  };

  // Handle price change for an aircraft
  const handlePriceChange = (optionId, value, field) => {
    // Update bid option property - onUpdate expects (optionId, field, value)
    if (onUpdate) {
      onUpdate(optionId, field, value);
    }
  };

  // Handle edit pricing button click
  const handleEditPricing = (aircraft) => {
    setSelectedAircraftForEdit(aircraft);
    setShowEditPricing(true);
  };

  // Handle save pricing from drawer
  const handleSavePricing = (optionId, updates) => {
    if (onBatchUpdate) {
      // Use batch update for better performance
      onBatchUpdate(optionId, updates);
    } else if (onUpdate) {
      // Fallback to individual updates
      Object.entries(updates).forEach(([field, value]) => {
        onUpdate(optionId, field, value);
      });
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
      {/* Section Header - Always Visible */}
      <button
        type="button"
        onClick={onToggle}
        disabled={!isUnlocked}
        className={cn(
          'w-full flex items-center justify-between p-6 text-left transition-colors',
          (!isUnlocked) && 'cursor-not-allowed opacity-60'
        )}
      >
        <div className="flex items-center gap-3 flex-1">
          <RiPlaneLine className="w-5 h-5 text-gray-700 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-gray-900">Aircraft Options and Pricing</h3>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isComplete && (
            <div className="flex items-center justify-center w-6 h-6 rounded-full">
              <RiCheckboxCircleLine className="w-6 h-6 text-primary-500" />
            </div>
          )}
        </div>
      </button>

      {/* Section Content - Shown when expanded */}
      {isExpanded && (
        <div className="px-6 pb-6">
          <div data-testid="section-content">
        {/* Loading State */}
        {isDataLoading && (
          <div className="flex flex-col items-center justify-center py-12">
            <RiLoader4Line
              data-testid="loading-spinner"
              className="h-8 w-8 animate-spin text-blue-600 mb-3"
              aria-hidden="true"
            />
            <p className="text-gray-600" aria-live="polite">
              Loading aircraft options...
            </p>
            {/* Loading Skeletons */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6 w-full">
              {[1, 2].map(i => (
                <div
                  key={i}
                  data-testid="aircraft-skeleton"
                  className="border border-gray-200 rounded-lg p-4 animate-pulse"
                >
                  <div className="h-32 bg-gray-200 rounded mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!isDataLoading && (!formData.bidOptions || formData.bidOptions.length === 0) && (
          <div className="flex flex-col items-center justify-center py-5 shadow-sm rounded-2xl border border-gray-100">
            <img src={EmptyState2} alt="No quotes" className="w-30 h-30 mx-auto" />
            <p className="text-gray-600 mt-2">
              No aircrafts added yet
            </p>
          </div>
        )}

        {/* Aircraft Slider */}
        {!isDataLoading && formData.bidOptions && formData.bidOptions.length > 0 && (
          <div
            ref={aircraftSliderRef}
            data-testid="aircraft-grid"
            aria-label="Available aircraft options"
            className="flex gap-4 overflow-x-auto pt-3 pb-2 -mx-6 px-6 cursor-grab [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
            onMouseDown={handleMouseDown}
            onMouseLeave={handleMouseLeave}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
          >
            {formData.bidOptions
              .map(aircraftItem => (
                <div key={aircraftItem.bid_option_id} className="flex-shrink-0 w-80 self-stretch">
                  <AircraftCard
                    aircraft={applyBufferedProfile(aircraftItem)}
                    passengerCount={formData.flights?.flights?.[0]?.passengerCount || 1}
                    flightType={formData.flights?.type || 'one-way'}
                    flightsLength={formData.flights?.flights?.length || 1}
                    operatorNameOverride={
                      (formData?.operatorNameOverrides || {})[aircraftItem?.aircraft?.aircraft_id]
                    }
                    onPriceChange={handlePriceChange}
                    onEditPricing={handleEditPricing}
                    onRemove={onRemove}
                    priceInvalid={
                      unpricedFlaggedIds.has(aircraftItem.bid_option_id) && isUnpriced(aircraftItem)
                    }
                  />
                </div>
              ))}
          </div>
        )}

        {canAddTails && (
          <button
            type="button"
            onClick={() => setShowSearchAircraft(true)}
            className={cn(
              'w-full mt-5 px-4 py-3 rounded-full text-sm font-medium transition-all border border-primary-700',             'bg-white rounded-full text-primary-700 text-md',
              'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
              'flex items-center justify-center gap-2'
            )}
          >
            <RiAddLine className="w-4 h-4" />
            Add Aircraft
          </button>
        )}

        </div>

          {/* Navigation Buttons */}
          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={onBack}
              className={cn(
                'flex-1 px-4 py-3 rounded-full text-sm font-medium transition-all',
                'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                'flex items-center justify-center gap-2',
              )}
            >
              <RiArrowUpLine className="w-4 h-4" />
              Back
            </button>
            <button
              type="button"
              onClick={handleNext}
              className={cn(
                'flex-1 px-4 py-3 rounded-full text-sm font-medium transition-all',
                'focus:outline-none focus:ring-0 focus:ring-blue-0 focus:ring-offset-0',
                'flex items-center justify-center gap-2',
                (!formData?.bidOptions || !formData.bidOptions?.length > 0)
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-accent text-primary-500 hover:bg-accent',
              )}
              disabled={!formData?.bidOptions || !formData.bidOptions?.length > 0}
            >
              Next
              <RiArrowDownSLine className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Search Aircraft Modal */}
      <Modal
        isOpen={showSearchAircraft}
        onClose={() => setShowSearchAircraft(false)}
        size="md"
        title="Search Aircraft"
        headerClassName="!border-0"
        closeOnOutsideClick={false}
      >
        <SearchAircraft
          showHeader={false}
          onClose={() => setShowSearchAircraft(false)}
          addtails="add"
          onAddManually={canAddAircraftManually ? (() => {
            // Close the search modal first so the drawer isn't fighting
            // the modal's focus trap.
            setShowSearchAircraft(false);
            setShowAddManually(true);
          }) : undefined}
        />
      </Modal>

      {/* Add Aircraft Manually Drawer.
          In Quick Quote → drawer falls back to its default (append to
          shortlist). In edit / duplicate / trip-group contexts we pass
          `onCreated` so the drawer skips the shortlist and hands the
          fully-hydrated aircraft up here, where it becomes a new
          bid_option on formData.bidOptions. */}
      <AddAircraftManuallyDrawer
        isOpen={showAddManually}
        onClose={() => setShowAddManually(false)}
        passengerCount={formData.flights?.flights?.[0]?.passengerCount || 6}
        flightType={formData.flights?.type || 'one-way'}
        flightsLength={formData.flights?.flights?.length || 1}
        onCreated={canAddTails && !isQuickQuote && onAppendBidOption
          ? (hydratedAircraft) => onAppendBidOption(hydratedAircraft)
          : undefined}
      />

      {/* Edit Pricing Drawer */}
      <EditPricingDrawer
        isOpen={showEditPricing}
        onClose={() => setShowEditPricing(false)}
        aircraft={selectedAircraftForEdit}
        passengerCount={formData.flights?.flights?.[0]?.passengerCount || 6}
        flightType={formData.flights?.type || 'one-way'}
        flightsLength={formData.flights?.flights?.length || 1}
        onSave={handleSavePricing}
        onSaveAircraftProfile={onSaveAircraftProfile}
        onSaveOperatorNameOverride={onSaveOperatorNameOverride}
        bufferedProfile={
          selectedAircraftForEdit?.aircraft?.aircraft_id in aircraftProfiles
            ? aircraftProfiles[selectedAircraftForEdit.aircraft.aircraft_id]
            : undefined
        }
        bufferedOperatorName={
          (formData?.operatorNameOverrides || {})[selectedAircraftForEdit?.aircraft?.aircraft_id]
        }
      />
    </div>
  );
};

export default AircraftOptionsSection;
